import React from "react";
import { Box, Typography, Card, CardMedia } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FONT_FAMILY_SERIF } from "../../theme";
import { getResponsiveImageAttributes } from "../../utils/imageUtils";

export interface Gallery {
  id: string;
  title: string;
  thumbnail?: string; // For navigation cards
  coverImage?: string; // Optional - may not have a cover image
  date: string;
  category: string;
  imageCount?: number;
  description?: string;
}

interface GalleryCardProps {
  gallery: Gallery;
}

export const GalleryCard: React.FC<GalleryCardProps> = ({ gallery }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card
        component={Link}
        to={`/gallery/${gallery.id}`}
        sx={{
          position: "relative",
          display: "block",
          textDecoration: "none",
          borderRadius: 0,
          boxShadow: "none",
          transition: (theme) =>
            `box-shadow ${theme.custom.transitions.standard}`,
          "&:hover": {
            boxShadow: (theme) => theme.custom.shadows.card,
          },
        }}
      >
        {/* Image */}
        <Box
          sx={{
            position: "relative",
            paddingTop: "66.67%", // 3:2 aspect ratio
            backgroundColor: "grey.200",
          }}
        >
          {gallery.coverImage && (
            <CardMedia
              component="img"
              alt={`${gallery.title} - Photography gallery by Mariah Blue`}
              {...(gallery.coverImage.includes("cloudinary.com")
                ? {
                    ...getResponsiveImageAttributes(gallery.coverImage, {
                      widths: [400, 800, 1200],
                      sizes: "(max-width: 600px) 100vw, (max-width: 960px) 50vw, 33vw",
                      defaultSize: 800,
                    }),
                  }
                : { image: gallery.coverImage })}
              loading="lazy"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: (theme) =>
                  `transform ${theme.custom.transitions.medium}`,
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
          )}

          {/* Overlay with Title and Description - Always Visible, Center Aligned */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: (theme) =>
                `linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: { xs: 3, md: 4 },
              textAlign: "center",
              transition: (theme) =>
                `background ${theme.custom.transitions.standard}`,
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%)`,
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Cinzel Decorative", serif',
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                fontWeight: 400,
                color: "white",
                mb: gallery.description ? { xs: 1, md: 1.5 } : 0,
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
              }}
            >
              {gallery.title}
            </Typography>

            {gallery.description && (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  maxWidth: { xs: "100%", md: "80%" },
                  lineHeight: { xs: 1.5, md: 1.6 },
                  textShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
                }}
              >
                {gallery.description}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};
